// src/utils/memberSheetImport.js
//
// Leitura e conferência das planilhas de importação de membros.
//
// São dois arquivos separados — alunos e professores — porque na escola os dois
// cadastros vivem em sistemas diferentes (secretaria acadêmica × RH) e porque
// cada modelo já chega com o perfil certo preenchido, evitando repetir a mesma
// célula 200 vezes. A coluna "Perfil" continua existindo nos dois para as
// exceções: um coordenador no meio da planilha de professores é aceito.
//
// A SENHA NÃO ESTÁ NA PLANILHA. Ela é digitada uma vez na tela de importação e
// vale para todos os registros daquele arquivo — a escola não deveria inventar
// 200 senhas numa coluna, e senha em planilha circula por e-mail e fica em
// pasta compartilhada. Quem precisar trocar usa "Esqueci minha senha"
// (POST /auth/forgot-password).
//
// A planilha de ALUNOS tem a coluna "Turma" (opcional): o nome de um grupo do
// titular em que o aluno entra junto com o cadastro. Quem cria/reutiliza o
// grupo é o backend, dentro da transação do lote (ver ManagedAccountService.
// LinkBatchItemsToGroupsAsync) — aqui só se lê a célula, normaliza e confere.
// A planilha de professores não tem a coluna: professor entra em turma pela
// tela de grupos, onde escolhe várias de uma vez.
//
// Todas as regras de campo vêm de utils/memberValidation.js, as MESMAS do
// formulário manual — ver o cabeçalho de lá.

import {
  DEFAULT_RELATIONSHIP_TYPE,
  PRIVACY_VERSION,
  TERMS_VERSION,
  calculateAge,
  capitalizeFirst,
  getTodayLocalDateString,
  resolveConsentText,
  validateBirthDate,
  validateEmail,
} from "./memberValidation";

// ─── Contrato do modelo ──────────────────────────────────────────────────────

// Versão por tipo de planilha, não uma só: a coluna Turma subiu o modelo de
// alunos para v2 e o de professores não mudou — subir os dois faria a escola
// que baixou o modelo de professores ontem receber "modelo desatualizado".
export const SHEET_TEMPLATE_VERSIONS = {
  alunos: "v2",
  professores: "v1",
};

// Aba lida. O modelo tem outras duas (Instruções e Listas) que são ignoradas.
const DATA_SHEET_NAME = "Dados";

// Célula onde o modelo grava a própria versão, para avisar quando a escola
// reaproveitar um arquivo de uma safra anterior em vez de baixar o novo.
const VERSION_SHEET_NAME = "Instruções";
const VERSION_CELL = "B1";

// Quantas linhas o modelo aceita. Acima disso o arquivo quase certamente não é
// uma planilha de turma, e a conferência na tela fica impraticável.
export const MAX_SHEET_ROWS = 1000;

/**
 * Os dois tipos de importação. `defaultProfile` é só o padrão da coluna Perfil
 * quando a célula vem vazia — nunca sobrescreve o que a escola escreveu.
 * `hasGroupColumn` liga a leitura da coluna Turma: só a planilha de alunos a
 * tem, e uma coluna "Turma" na de professores é ignorada de propósito.
 */
export const IMPORT_KINDS = [
  {
    key: "alunos",
    label: "Alunos",
    icon: "👨‍🎓",
    defaultProfile: "Aluno",
    hasGroupColumn: true,
    templateVersion: SHEET_TEMPLATE_VERSIONS.alunos,
    templateUrl: `/modelos/modelo-alunos-${SHEET_TEMPLATE_VERSIONS.alunos}.xlsx`,
    templateName: `modelo-alunos-${SHEET_TEMPLATE_VERSIONS.alunos}.xlsx`,
  },
  {
    key: "professores",
    label: "Professores",
    icon: "👨‍🏫",
    defaultProfile: "Professor",
    hasGroupColumn: false,
    templateVersion: SHEET_TEMPLATE_VERSIONS.professores,
    templateUrl: `/modelos/modelo-professores-${SHEET_TEMPLATE_VERSIONS.professores}.xlsx`,
    templateName: `modelo-professores-${SHEET_TEMPLATE_VERSIONS.professores}.xlsx`,
  },
];

export function getImportKind(key) {
  return IMPORT_KINDS.find((k) => k.key === key) ?? IMPORT_KINDS[0];
}

// ─── Cabeçalhos ──────────────────────────────────────────────────────────────

// Aceita variações porque escola renomeia cabeçalho: some o acento, vira
// maiúscula, ganha um "(obrigatório)" no fim. Barrar o arquivo por isso seria
// hostil quando o dado está todo lá.
const HEADER_ALIASES = {
  fullName: ["nome completo", "nome", "aluno", "professor", "nome do aluno", "nome do professor"],
  userName: ["nome de usuario", "usuario", "login", "user", "nome de usuário"],
  email: ["e-mail", "email", "e mail", "endereco de e-mail", "endereço de e-mail"],
  birthDate: ["data de nascimento", "nascimento", "data nascimento", "dt nascimento"],
  relationshipType: ["perfil", "papel", "funcao", "função", "cargo", "tipo"],
  groupName: ["turma", "classe", "sala", "serie/turma", "série/turma", "grupo"],
};

// Colunas que só existem em alguns modelos. Fora deles o alias não é nem
// procurado — assim uma coluna "Turma" na planilha de professores passa
// batida em vez de virar vínculo escondido.
const KIND_ONLY_COLUMNS = { groupName: (kind) => kind.hasGroupColumn };

// Sem estes dois não dá para criar conta nenhuma — o resto tem padrão ou é
// opcional.
const REQUIRED_COLUMNS = ["userName", "email"];

// Limite do banco (groups.group_name). Acima disso a célula é quase certamente
// uma frase colada no lugar errado.
export const MAX_GROUP_NAME_LENGTH = 255;

function normalizeHeader(value) {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\(.*?\)/g, "")
    .replace(/[*:]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function mapHeaderRow(cells, kind) {
  const map = {};
  cells.forEach((cell, index) => {
    const normalized = normalizeHeader(cell);
    if (!normalized) return;
    for (const [field, aliases] of Object.entries(HEADER_ALIASES)) {
      if (map[field] !== undefined) continue;
      if (KIND_ONLY_COLUMNS[field] && !KIND_ONLY_COLUMNS[field](kind)) continue;
      if (aliases.some((alias) => normalizeHeader(alias) === normalized)) {
        map[field] = index;
        return;
      }
    }
  });
  return map;
}

// ─── Normalização de células ─────────────────────────────────────────────────

function cellToText(value) {
  if (value === null || value === undefined) return "";
  if (value instanceof Date) return "";
  return String(value).trim();
}

/**
 * Nome de turma como o backend compara. É a MESMA régua de
 * ManagedAccountService.NormalizeGroupName — divergir aqui faria a conferência
 * dizer "será criada" para uma turma que o lote reutiliza (ou o contrário).
 *
 *  0. travessão (—) vira hífen — regra de todo nome de grupo no sistema
 *     (GroupNameSanitizer no backend); o caractere não existe no teclado e
 *     atrapalhava buscar e comparar nomes;
 *  1. espaços nas pontas fora, internos colapsados;
 *  2. "9A", "9 a", "9°A", "9ºa" → "9º A": série + letra é o jeito mais comum
 *     de nomear turma, e cada secretaria digita de um jeito. Só esse padrão
 *     (1–2 dígitos + letra única) é reescrito; "9º ano A", "1º EM" e "Turma B"
 *     ficam como estão.
 */
export function normalizeGroupName(value) {
  const collapsed = String(value ?? "")
    .replace(/—/g, "-")
    .replace(/\s+/g, " ")
    .trim();
  const serie = collapsed.match(/^(\d{1,2})\s*[º°]?\s*([A-Za-z])$/);
  if (serie) return `${serie[1]}º ${serie[2].toUpperCase()}`;
  return collapsed;
}

export function groupNameKey(value) {
  return normalizeGroupName(value).toLowerCase();
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

function fromDateParts(year, month, day) {
  if (!year || !month || !day) return null;
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return `${year}-${pad2(month)}-${pad2(day)}`;
}

/**
 * Data de nascimento para "AAAA-MM-DD" (o formato que o input date e o backend
 * usam), a partir das três formas que a célula pode chegar:
 *
 *  - Date, quando a célula é do tipo data no Excel (o caminho normal do modelo)
 *  - número, o serial do Excel, quando o leitor não converteu
 *  - texto, quando a escola digitou "05/03/2011" ou colou de outro sistema
 *
 * Devolve null quando não dá para entender — quem transforma isso em mensagem
 * é a conferência, que sabe o número da linha.
 */
function normalizeBirthDate(value, XLSX) {
  if (value === null || value === undefined || value === "") return null;

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return fromDateParts(
      value.getFullYear(),
      value.getMonth() + 1,
      value.getDate()
    );
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    const parsed = XLSX?.SSF?.parse_date_code?.(value);
    if (parsed && parsed.y) return fromDateParts(parsed.y, parsed.m, parsed.d);
    return null;
  }

  const text = String(value).trim();
  if (!text) return null;

  // DD/MM/AAAA ou DD-MM-AAAA (o que uma escola brasileira digita)
  const br = text.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{4})$/);
  if (br) return fromDateParts(Number(br[3]), Number(br[2]), Number(br[1]));

  // AAAA-MM-DD (o que vem colado de outro sistema)
  const iso = text.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (iso) return fromDateParts(Number(iso[1]), Number(iso[2]), Number(iso[3]));

  return null;
}

// ─── Leitura do arquivo ──────────────────────────────────────────────────────

export class SheetImportError extends Error {}

/**
 * Lê um .xlsx e devolve as linhas cruas, já com número de linha da planilha
 * (para a conferência poder dizer "linha 47" e a pessoa achar no Excel).
 *
 * O SheetJS entra por import dinâmico: são ~400 KB que só fazem sentido para
 * quem abriu a aba de importação, e a maioria dos titulares cadastra na mão.
 *
 * @returns {Promise<{templateVersion: string|null, rows: Array, skippedRows: number}>}
 */
export async function readMemberSheet(file, kindKey) {
  const XLSX = await import("xlsx");
  const kind = getImportKind(kindKey);

  let workbook;
  try {
    const buffer = await file.arrayBuffer();
    workbook = XLSX.read(buffer, { type: "array", cellDates: true });
  } catch {
    throw new SheetImportError(
      "Não foi possível ler o arquivo. Confirme que é um .xlsx gerado a partir do modelo."
    );
  }

  const sheetName =
    workbook.SheetNames.find(
      (name) => normalizeHeader(name) === normalizeHeader(DATA_SHEET_NAME)
    ) ?? workbook.SheetNames[0];

  const sheet = sheetName ? workbook.Sheets[sheetName] : null;
  if (!sheet) {
    throw new SheetImportError(
      `A planilha não tem a aba "${DATA_SHEET_NAME}". Baixe o modelo e preencha nela.`
    );
  }

  // Versão gravada pelo modelo. Ausente = arquivo montado à mão, o que é
  // aceito; diferente = modelo de outra safra, e aí vale avisar.
  const versionSheet = workbook.Sheets[VERSION_SHEET_NAME];
  const templateVersion = versionSheet?.[VERSION_CELL]?.v
    ? String(versionSheet[VERSION_CELL].v).trim()
    : null;

  const grid = XLSX.utils.sheet_to_json(sheet, {
    header: 1,
    raw: true,
    blankrows: true,
    defval: null,
  });

  // sheet_to_json devolve a grade a partir do início do range, que nem sempre
  // é a linha 1 — sem este offset o "linha 47" da mensagem apontaria errado.
  const range = sheet["!ref"] ? XLSX.utils.decode_range(sheet["!ref"]) : null;
  const firstSheetRow = (range?.s?.r ?? 0) + 1;

  // Tolera um título antes do cabeçalho: procura nas primeiras linhas a que
  // realmente mapeia as colunas obrigatórias.
  let headerIndex = -1;
  let columns = {};
  for (let i = 0; i < Math.min(grid.length, 10); i++) {
    const candidate = mapHeaderRow(grid[i] ?? [], kind);
    if (REQUIRED_COLUMNS.every((field) => candidate[field] !== undefined)) {
      headerIndex = i;
      columns = candidate;
      break;
    }
  }

  if (headerIndex === -1) {
    throw new SheetImportError(
      'Não encontrei as colunas "Nome de usuário" e "E-mail" no cabeçalho. Use o modelo sem renomear as colunas.'
    );
  }

  const rows = [];
  let skippedRows = 0;
  let pendingBlankRows = 0;

  for (let i = headerIndex + 1; i < grid.length; i++) {
    const cells = grid[i] ?? [];
    const pick = (field) =>
      columns[field] === undefined ? null : cells[columns[field]];

    const fullName = cellToText(pick("fullName"));
    const userName = cellToText(pick("userName"));
    const email = cellToText(pick("email"));
    const rawBirthDate = pick("birthDate");
    const relationshipType = cellToText(pick("relationshipType"));
    const groupName = normalizeGroupName(cellToText(pick("groupName")));

    // Perfil NÃO entra nesta conta: o modelo já vem com o perfil padrão
    // preenchido nas primeiras 60 linhas, e Perfil sozinho não cria conta —
    // contá-lo transformava o rabo do modelo em dezenas de "linhas com erro".
    const isBlank =
      !fullName &&
      !userName &&
      !email &&
      !groupName &&
      (rawBirthDate === null || rawBirthDate === undefined || rawBirthDate === "");

    // Linha em branco é rastro de edição, não erro.
    //
    // Só contam as que ficam ENTRE linhas preenchidas. O modelo aplica formato
    // e validação até a linha 1001, o que estende o range da planilha: contar
    // o rabo vazio faria a tela anunciar "992 linhas ignoradas" num arquivo com
    // 9 alunos. Por isso o contador é reconciliado a cada linha com conteúdo.
    if (isBlank) {
      pendingBlankRows++;
      continue;
    }
    skippedRows += pendingBlankRows;
    pendingBlankRows = 0;

    if (rows.length >= MAX_SHEET_ROWS) {
      throw new SheetImportError(
        `A planilha passa de ${MAX_SHEET_ROWS} linhas preenchidas. Divida em arquivos menores.`
      );
    }

    rows.push({
      id: `${kind.key}:${i}`,
      kind: kind.key,
      sourceFile: file.name,
      rowNumber: firstSheetRow + i,
      fullName,
      userName,
      email,
      birthDate: normalizeBirthDate(rawBirthDate, XLSX),
      // Guarda o texto cru para conseguir dizer "não entendi esta data" em vez
      // de tratar data ilegível como célula vazia.
      rawBirthDate: rawBirthDate instanceof Date ? "" : cellToText(rawBirthDate),
      relationshipType: capitalizeFirst(relationshipType),
      groupName,
    });
  }

  if (rows.length === 0) {
    throw new SheetImportError(
      "A planilha não tem nenhuma linha preenchida abaixo do cabeçalho."
    );
  }

  return { templateVersion, rows, skippedRows, sheetName };
}

// ─── Conferência ─────────────────────────────────────────────────────────────

function normalizeKey(value) {
  return (value ?? "").trim().toLowerCase();
}

/**
 * Passa a régua em todas as linhas importadas de uma vez — as duas planilhas
 * juntas, porque aluno repetido na planilha de professores só aparece olhando
 * as duas.
 *
 * @param {Array} rows           linhas de readMemberSheet (pode misturar tipos)
 * @param {object} options
 * @param {Array}  options.stagedItems       itens já na "Lista para confirmar"
 * @param {Set}    options.existingEmails    do precheck no servidor (minúsculas)
 * @param {Set}    options.existingUserNames do precheck no servidor (minúsculas)
 * @param {Set}    options.reservedGroupNames do precheck (chaves de groupNameKey)
 * @returns {Array} as mesmas linhas com `errors: string[]` e `age`
 */
export function analyzeImportRows(rows, options = {}) {
  const {
    stagedItems = [],
    existingEmails = new Set(),
    existingUserNames = new Set(),
    reservedGroupNames = new Set(),
    referenceDate = getTodayLocalDateString(),
  } = options;

  const stagedEmails = new Set(
    stagedItems.map((i) => normalizeKey(i.payload?.email)).filter(Boolean)
  );
  const stagedUserNames = new Set(
    stagedItems
      .filter((i) => i.type === "new")
      .map((i) => normalizeKey(i.payload?.userName))
      .filter(Boolean)
  );

  // Primeira ocorrência de cada chave, para acusar a repetição na SEGUNDA linha
  // e apontar a linha original ("repetido da linha 12").
  const emailFirstRow = new Map();
  const userNameFirstRow = new Map();

  return rows.map((row) => {
    const errors = [];
    const kind = getImportKind(row.kind);

    // ── Nome de usuário ──
    const userNameKey = normalizeKey(row.userName);
    if (!userNameKey) {
      errors.push("Nome de usuário é obrigatório.");
    } else if (/\s/.test(row.userName.trim())) {
      errors.push("Nome de usuário não pode ter espaços.");
    } else if (userNameFirstRow.has(userNameKey)) {
      errors.push(
        `Nome de usuário repetido (já usado na linha ${userNameFirstRow.get(userNameKey)}).`
      );
    } else if (stagedUserNames.has(userNameKey)) {
      errors.push("Nome de usuário já está na lista para confirmar.");
    } else if (existingUserNames.has(userNameKey)) {
      errors.push("Nome de usuário já existe na plataforma.");
    }
    if (userNameKey && !userNameFirstRow.has(userNameKey)) {
      userNameFirstRow.set(userNameKey, row.rowNumber);
    }

    // ── E-mail ──
    const emailKey = normalizeKey(row.email);
    if (!emailKey) {
      errors.push("E-mail é obrigatório.");
    } else {
      const emailMessage = validateEmail(row.email);
      if (emailMessage) {
        errors.push(emailMessage);
      } else if (emailFirstRow.has(emailKey)) {
        errors.push(
          `E-mail repetido (já usado na linha ${emailFirstRow.get(emailKey)}).`
        );
      } else if (stagedEmails.has(emailKey)) {
        errors.push("E-mail já está na lista para confirmar.");
      } else if (existingEmails.has(emailKey)) {
        errors.push("E-mail já existe na plataforma.");
      }
    }
    if (emailKey && !emailFirstRow.has(emailKey)) {
      emailFirstRow.set(emailKey, row.rowNumber);
    }

    // ── Data de nascimento ──
    if (!row.birthDate) {
      errors.push(
        row.rawBirthDate
          ? `Data de nascimento não reconhecida ("${row.rawBirthDate}"). Use DD/MM/AAAA.`
          : "Data de nascimento é obrigatória."
      );
    } else {
      const birthMessage = validateBirthDate(row.birthDate, referenceDate);
      if (birthMessage) errors.push(birthMessage);
    }

    // ── Turma ── (opcional; só a planilha de alunos tem a coluna)
    const groupName = kind.hasGroupColumn ? normalizeGroupName(row.groupName) : "";
    if (groupName.length > MAX_GROUP_NAME_LENGTH) {
      errors.push(`Turma com mais de ${MAX_GROUP_NAME_LENGTH} caracteres.`);
    } else if (groupName && reservedGroupNames.has(groupNameKey(groupName))) {
      errors.push(`"${groupName}" é um nome de grupo reservado pelo sistema. Use outro nome de turma.`);
    }

    return {
      ...row,
      relationshipType: row.relationshipType || kind.defaultProfile,
      groupName,
      age: row.birthDate ? calculateAge(row.birthDate, referenceDate) : null,
      errors,
    };
  });
}

/**
 * Resumo das turmas da conferência: uma entrada por nome (a menos de
 * maiúsculas/espaços), com quantas linhas prontas apontam para ela e se o
 * titular já tem esse grupo. É o que deixa um "9 B" com um aluno aparecer ao
 * lado do "9º B" com trinta — o erro de digitação que, sem isso, só viraria
 * visível como grupo fantasma semanas depois.
 *
 * @param {Array} rows              linhas de analyzeImportRows
 * @param {Set}   existingGroupNames do precheck (chaves de groupNameKey)
 * @returns {Array<{name: string, count: number, exists: boolean}>}
 */
export function summarizeImportGroups(rows, existingGroupNames = new Set()) {
  const byKey = new Map();
  for (const row of rows) {
    if (!row.groupName || row.errors.length > 0) continue;
    const key = groupNameKey(row.groupName);
    const entry = byKey.get(key);
    if (entry) {
      entry.count += 1;
    } else {
      byKey.set(key, {
        name: row.groupName,
        count: 1,
        exists: existingGroupNames.has(key),
      });
    }
  }
  return [...byKey.values()].sort((a, b) =>
    a.name.localeCompare(b.name, "pt-BR", { numeric: true })
  );
}

/**
 * Converte uma linha conferida no item da "Lista para confirmar" — o MESMO
 * formato que os modos "Nova conta" e "Vincular existente" produzem, para a
 * importação não virar um caminho paralelo até a API.
 *
 * A senha vem de fora: é a senha padrão digitada para o arquivo.
 */
export function buildImportedItem(row, password, referenceDate = getTodayLocalDateString()) {
  const relationshipType =
    capitalizeFirst(row.relationshipType) ||
    getImportKind(row.kind).defaultProfile ||
    DEFAULT_RELATIONSHIP_TYPE;

  return {
    type: "new",
    label: row.fullName?.trim() || row.userName?.trim(),
    email: row.email?.trim(),
    status: "pending",
    errorReason: null,
    payload: {
      userName: row.userName.trim(),
      fullName: row.fullName?.trim() ?? "",
      email: row.email.trim(),
      password,
      birthDate: row.birthDate || null,
      relationshipType,
      groupName: normalizeGroupName(row.groupName) || null,
      // Por linha, não por arquivo: numa planilha de alunos convivem menores
      // (autorização do responsável) e maiores de 18 (autorização
      // institucional), e o texto fica gravado no vínculo.
      consentTextSnapshot: resolveConsentText(row.birthDate, referenceDate),
      termsVersion: TERMS_VERSION,
      privacyVersion: PRIVACY_VERSION,
    },
  };
}
